import { CSV_PREVIEW_MAX_COLUMNS, CSV_PREVIEW_MAX_ROWS } from './preview-limits'

export class PreviewFetchError extends Error {}
export class PreviewTooLargeError extends Error {}

export type PreviewSource = {
  url: string
  /** Asks the API for fresh access; used once when a signed URL expired. */
  refresh: () => Promise<string>
}

const EXPIRED_STATUSES = new Set([400, 401, 403])

/**
 * Fetches signed content. An expired signature is refreshed exactly once;
 * a second failure is reported instead of looping.
 */
export async function fetchPreview(
  source: PreviewSource,
  signal: AbortSignal,
): Promise<Response> {
  const request = (url: string) =>
    fetch(url, { signal, credentials: 'omit', cache: 'no-store' })
  let response = await request(source.url)
  if (EXPIRED_STATUSES.has(response.status))
    response = await request(await source.refresh())
  if (!response.ok) throw new PreviewFetchError(`Status ${response.status}`)

  return response
}

export async function readLimited(
  response: Response,
  limit: number,
): Promise<{ bytes: Uint8Array; truncated: boolean }> {
  const reader = response.body?.getReader()
  if (!reader) {
    const bytes = new Uint8Array(await response.arrayBuffer())

    return bytes.length > limit
      ? { bytes: bytes.subarray(0, limit), truncated: true }
      : { bytes, truncated: false }
  }
  const chunks: Array<Uint8Array> = []
  let total = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (total + value.length > limit) {
      chunks.push(value.subarray(0, limit - total))
      total = limit
      await reader.cancel()

      return { bytes: concat(chunks, total), truncated: true }
    }
    chunks.push(value)
    total += value.length
  }

  return { bytes: concat(chunks, total), truncated: false }
}

function concat(chunks: Array<Uint8Array>, total: number): Uint8Array {
  const bytes = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.length
  }

  return bytes
}

export function decodeText(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes)
}

const JSON_TOKEN = /"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/gs

/** Number literals outside of strings, in document order. */
function numberTokens(json: string): Array<string> {
  return (json.match(JSON_TOKEN) ?? []).filter((token) => token[0] !== '"')
}

/**
 * Pretty-prints complete JSON; anything else, or JSON whose numbers would not
 * survive a JavaScript Number round trip, is shown exactly as stored.
 */
export function formatJson(text: string, truncated: boolean): string {
  if (truncated) return text
  try {
    const formatted = JSON.stringify(JSON.parse(text), null, 2)
    const before = numberTokens(text)
    const after = numberTokens(formatted)

    return before.length === after.length &&
      before.every((token, position) => token === after[position])
      ? formatted
      : text
  } catch {
    return text
  }
}

export type CsvPreview = {
  rows: Array<Array<string>>
  rowsTruncated: boolean
  columnsTruncated: boolean
}

function countDelimiter(header: string, delimiter: string): number {
  let inQuotes = false
  let count = 0
  for (const character of header) {
    if (character === '"') inQuotes = !inQuotes
    else if (!inQuotes && character === delimiter) count += 1
  }

  return count
}

/** Index of the first line break that is not inside a quoted field. */
function headerEnd(text: string): number {
  let inQuotes = false
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]
    if (character === '"') inQuotes = !inQuotes
    else if (!inQuotes && (character === '\n' || character === '\r'))
      return index
  }

  return text.length
}

function detectDelimiter(text: string): string {
  const header = text.slice(0, headerEnd(text))
  const counts = [',', ';', '\t'].map(
    (delimiter) => [delimiter, countDelimiter(header, delimiter)] as const,
  )

  return counts.reduce((best, item) => (item[1] > best[1] ? item : best))[0]
}

type CsvReader = {
  rows: Array<Array<string>>
  row: Array<string>
  field: string
  quoted: boolean
  columnsTruncated: boolean
}

function endField(reader: CsvReader) {
  if (reader.row.length < CSV_PREVIEW_MAX_COLUMNS) reader.row.push(reader.field)
  else reader.columnsTruncated = true
  reader.field = ''
}

function endRow(reader: CsvReader) {
  endField(reader)
  reader.rows.push(reader.row)
  reader.row = []
}

/** Inside quotes: returns how many following characters were used. */
function consumeQuoted(
  reader: CsvReader,
  character: string,
  next: string | undefined,
): number {
  if (character !== '"') {
    reader.field += character

    return 0
  }
  if (next !== '"') {
    reader.quoted = false

    return 0
  }
  reader.field += '"'

  return 1
}

/** Handles one character and returns how many following characters it used. */
function consume(
  reader: CsvReader,
  character: string,
  next: string | undefined,
  delimiter: string,
): number {
  if (reader.quoted) return consumeQuoted(reader, character, next)
  if (character === '"' && reader.field === '') reader.quoted = true
  else if (character === delimiter) endField(reader)
  else if (character === '\n' || character === '\r') {
    endRow(reader)

    return character === '\r' && next === '\n' ? 1 : 0
  } else reader.field += character

  return 0
}

/**
 * Small RFC 4180 reader bounded in rows and columns so a huge sheet cannot
 * freeze the page. When the byte limit cut the text, the partial last row is
 * dropped.
 */
export function parseCsv(text: string, cutOff: boolean): CsvPreview {
  const delimiter = detectDelimiter(text)
  const reader: CsvReader = {
    rows: [],
    row: [],
    field: '',
    quoted: false,
    columnsTruncated: false,
  }
  let rowsTruncated = false
  for (let index = 0; index < text.length; index += 1) {
    if (reader.rows.length >= CSV_PREVIEW_MAX_ROWS) {
      rowsTruncated = true
      break
    }
    index += consume(reader, text[index], text[index + 1], delimiter)
  }
  const pending = reader.field !== '' || reader.row.length > 0
  if (pending && cutOff) rowsTruncated = true
  else if (pending && !rowsTruncated) endRow(reader)

  return {
    rows: reader.rows,
    rowsTruncated,
    columnsTruncated: reader.columnsTruncated,
  }
}
